import { toast as sonnerToast, ExternalToast } from 'sonner';

/**
 * Web Audio API Sound Synthesizer for Scrudix Notifications
 * Generates crisp, pleasant notification chimes without needing external audio files.
 */
class NotificationSoundService {
  private ctx: AudioContext | null = null;
  private soundEnabled: boolean = true;
  private volume: number = 0.35;
  private userInteracted: boolean = false;

  constructor() {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('scrudix_notification_sound_enabled');
      this.soundEnabled = saved !== null ? saved === 'true' : true;

      // Unlock AudioContext on first user click or keypress
      const unlockAudio = () => {
        if (!this.userInteracted) {
          this.userInteracted = true;
          this.initAudioContext();
          window.removeEventListener('click', unlockAudio);
          window.removeEventListener('keydown', unlockAudio);
          window.removeEventListener('touchstart', unlockAudio);
        }
      };

      window.addEventListener('click', unlockAudio, { passive: true });
      window.addEventListener('keydown', unlockAudio, { passive: true });
      window.addEventListener('touchstart', unlockAudio, { passive: true });
    }
  }

  private initAudioContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  public isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  public setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    if (typeof window !== 'undefined') {
      localStorage.setItem('scrudix_notification_sound_enabled', String(enabled));
    }
  }

  public toggleSound(): boolean {
    const next = !this.soundEnabled;
    this.setSoundEnabled(next);
    if (next) {
      this.play('info');
    }
    return next;
  }

  public setVolume(vol: number): void {
    this.volume = Math.max(0, Math.min(1, vol));
  }

  public play(type: 'success' | 'error' | 'info' | 'warning' | 'default' = 'info'): void {
    if (!this.soundEnabled) return;
    try {
      const ctx = this.initAudioContext();
      if (!ctx) return;

      const now = ctx.currentTime;
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(this.volume, now);
      masterGain.connect(ctx.destination);

      if (type === 'success') {
        // High pleasant triple chime (C5 -> E5 -> G5)
        this.playTone(ctx, masterGain, 523.25, now, 0.08, 'sine', 0.25); // C5
        this.playTone(ctx, masterGain, 659.25, now + 0.07, 0.12, 'sine', 0.3); // E5
        this.playTone(ctx, masterGain, 783.99, now + 0.14, 0.25, 'sine', 0.35); // G5
      } else if (type === 'error') {
        // Soft low double-tone warning (F4 -> C4)
        this.playTone(ctx, masterGain, 349.23, now, 0.1, 'triangle', 0.3); // F4
        this.playTone(ctx, masterGain, 261.63, now + 0.09, 0.22, 'triangle', 0.35); // C4
      } else if (type === 'warning') {
        // Double pulse warning chime (A4 -> B4)
        this.playTone(ctx, masterGain, 440.0, now, 0.09, 'sine', 0.25);
        this.playTone(ctx, masterGain, 493.88, now + 0.09, 0.18, 'sine', 0.3);
      } else {
        // Info / Default pop chime (D5 pop with smooth decay)
        this.playTone(ctx, masterGain, 587.33, now, 0.16, 'sine', 0.3);
      }
    } catch (e) {
      console.warn('[NotificationSound] Audio playback error:', e);
    }
  }

  private playTone(
    ctx: AudioContext,
    destination: GainNode,
    freq: number,
    startTime: number,
    duration: number,
    type: OscillatorType = 'sine',
    peakGain: number = 0.3
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, startTime);

    // Envelope: 12ms attack, smooth exponential decay
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(peakGain, startTime + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

    osc.connect(gain);
    gain.connect(destination);

    osc.start(startTime);
    osc.stop(startTime + duration + 0.05);
  }
}

export const notificationSound = new NotificationSoundService();

/**
 * Enhanced Toast Interface that triggers sound and applies rich styling
 */
export const notify = {
  success: (message: string | React.ReactNode, data?: ExternalToast) => {
    notificationSound.play('success');
    return sonnerToast.success(message, {
      className: 'scrudix-toast scrudix-toast-success',
      ...data,
    });
  },
  error: (message: string | React.ReactNode, data?: ExternalToast) => {
    notificationSound.play('error');
    return sonnerToast.error(message, {
      className: 'scrudix-toast scrudix-toast-error',
      ...data,
    });
  },
  info: (message: string | React.ReactNode, data?: ExternalToast) => {
    notificationSound.play('info');
    return sonnerToast.info(message, {
      className: 'scrudix-toast scrudix-toast-info',
      ...data,
    });
  },
  warning: (message: string | React.ReactNode, data?: ExternalToast) => {
    notificationSound.play('warning');
    return sonnerToast.warning(message, {
      className: 'scrudix-toast scrudix-toast-warning',
      ...data,
    });
  },
  message: (message: string | React.ReactNode, data?: ExternalToast) => {
    notificationSound.play('default');
    return sonnerToast.message(message, {
      className: 'scrudix-toast scrudix-toast-default',
      ...data,
    });
  },
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
  promise: sonnerToast.promise,
  custom: sonnerToast.custom,
};

// Auto-patch Sonner toast methods globally so ALL existing toast calls play sound automatically
if (typeof window !== 'undefined') {
  const patchSonnerToast = () => {
    const origSuccess = sonnerToast.success.bind(sonnerToast);
    const origError = sonnerToast.error.bind(sonnerToast);
    const origInfo = sonnerToast.info.bind(sonnerToast);
    const origWarning = sonnerToast.warning.bind(sonnerToast);
    const origMessage = sonnerToast.message.bind(sonnerToast);

    sonnerToast.success = (msg: any, data?: any) => {
      notificationSound.play('success');
      return origSuccess(msg, { className: 'scrudix-toast scrudix-toast-success', ...data });
    };

    sonnerToast.error = (msg: any, data?: any) => {
      notificationSound.play('error');
      return origError(msg, { className: 'scrudix-toast scrudix-toast-error', ...data });
    };

    sonnerToast.info = (msg: any, data?: any) => {
      notificationSound.play('info');
      return origInfo(msg, { className: 'scrudix-toast scrudix-toast-info', ...data });
    };

    sonnerToast.warning = (msg: any, data?: any) => {
      notificationSound.play('warning');
      return origWarning(msg, { className: 'scrudix-toast scrudix-toast-warning', ...data });
    };

    sonnerToast.message = (msg: any, data?: any) => {
      notificationSound.play('default');
      return origMessage(msg, { className: 'scrudix-toast scrudix-toast-default', ...data });
    };
  };

  try {
    patchSonnerToast();
  } catch (e) {
    console.warn('[NotificationService] Global toast patch error:', e);
  }
}

export default notify;
